import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface UserPresence {
  user_id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  online_at: string;
}

export function useSessionPresence(sessionId: string | undefined, currentUserId: string | null) {
  const [activeUsers, setActiveUsers] = useState<UserPresence[]>([]);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!sessionId || !currentUserId) {
      return;
    }

    const roomChannel = supabase.channel(`session_${sessionId}`);

    roomChannel
      .on('presence', { event: 'sync' }, () => {
        const state = roomChannel.presenceState();
        const users: UserPresence[] = [];
        
        Object.keys(state).forEach((key) => {
          const presences = state[key];
          presences.forEach((presence: any) => {
            users.push(presence);
          });
        });
        
        // Filter out current user
        setActiveUsers(users.filter(u => u.user_id !== currentUserId));
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('User joined:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('User left:', key, leftPresences);
      });

    roomChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        // Get current user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('email, name, avatar_url')
          .eq('id', currentUserId)
          .single();

        if (profile) {
          const userStatus: UserPresence = {
            user_id: currentUserId,
            email: profile.email,
            name: profile.name,
            avatar_url: profile.avatar_url,
            online_at: new Date().toISOString(),
          };

          await roomChannel.track(userStatus);
        }
      }
    });

    setChannel(roomChannel);

    return () => {
      if (channel) {
        channel.untrack();
        supabase.removeChannel(channel);
      }
    };
  }, [sessionId, currentUserId]);

  return { activeUsers };
}
